import { useState } from 'react';

interface NavigationProps {
  isScrolled: boolean;
  lang: 'uk' | 'ru';
  setLang: (lang: 'uk' | 'ru') => void;
  isDark: boolean;
  setIsDark: (v: boolean) => void;
}

const navLabels = {
  uk: {
    services: 'Послуги',
    about: 'Про мене',
    testimonials: 'Відгуки',
    contact: 'Контакти',
    cta: 'Записатися',
  },
  ru: {
    services: 'Услуги',
    about: 'Обо мне',
    testimonials: 'Отзывы',
    contact: 'Контакты',
    cta: 'Записаться',
  },
};

export default function Navigation({ isScrolled, lang, setLang, isDark, setIsDark }: NavigationProps) {
  const t = navLabels[lang];
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || menuOpen
          ? 'bg-white dark:bg-[#1A1A1A] shadow-sm dark:shadow-[0_1px_8px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/ecd0888d-80c2-406e-a1ef-57dfedd3f099.png"
              alt="Логотип"
              className="h-[45px] md:h-[54px] w-auto cursor-pointer"
              onClick={scrollToTop}
            />
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('services')} className="text-[#333333] dark:text-[#CCCCCC] text-[15px] tracking-wide hover:text-[#7EC8E3] dark:hover:text-[#7EC8E3] transition-colors duration-300 cursor-pointer whitespace-nowrap">{t.services}</button>
            <button onClick={() => scrollToSection('about')} className="text-[#333333] dark:text-[#CCCCCC] text-[15px] tracking-wide hover:text-[#7EC8E3] dark:hover:text-[#7EC8E3] transition-colors duration-300 cursor-pointer whitespace-nowrap">{t.about}</button>
            <button onClick={() => scrollToSection('testimonials')} className="text-[#333333] dark:text-[#CCCCCC] text-[15px] tracking-wide hover:text-[#7EC8E3] dark:hover:text-[#7EC8E3] transition-colors duration-300 cursor-pointer whitespace-nowrap">{t.testimonials}</button>
            <button onClick={() => scrollToSection('contact')} className="text-[#333333] dark:text-[#CCCCCC] text-[15px] tracking-wide hover:text-[#7EC8E3] dark:hover:text-[#7EC8E3] transition-colors duration-300 cursor-pointer whitespace-nowrap">{t.contact}</button>
          </div>

          <div className="flex items-center gap-3">
            {/* Language selector */}
            <div className="flex items-center bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-full p-1">
              <button
                onClick={() => setLang('uk')}
                className={`px-3 py-1 rounded-full text-[13px] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  lang === 'uk'
                    ? 'bg-white dark:bg-[#3A3A3A] text-[#333333] dark:text-[#EEEEEE] shadow-sm'
                    : 'text-[#999999] hover:text-[#555555] dark:hover:text-[#BBBBBB]'
                }`}
              >
                УКР
              </button>
              <button
                onClick={() => setLang('ru')}
                className={`px-3 py-1 rounded-full text-[13px] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  lang === 'ru'
                    ? 'bg-white dark:bg-[#3A3A3A] text-[#333333] dark:text-[#EEEEEE] shadow-sm'
                    : 'text-[#999999] hover:text-[#555555] dark:hover:text-[#BBBBBB]'
                }`}
              >
                РУС
              </button>
            </div>

            {/* Theme toggle — desktop only */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="hidden md:flex w-9 h-9 items-center justify-center rounded-full bg-[#F1F1F1] dark:bg-[#2A2A2A] cursor-pointer transition-colors duration-200"
              aria-label="Переключить тему"
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-[18px] text-[#333333] dark:text-[#EEEEEE]`}></i>
            </button>

            {/* Desktop CTA */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden md:block px-6 py-2.5 border-2 border-[#7EC8E3] text-[#7EC8E3] rounded-full text-[15px] font-medium hover:bg-[#7EC8E3] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t.cta}
            </button>

            {/* Burger button */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-[#F1F1F1] dark:bg-[#2A2A2A] cursor-pointer"
              aria-label="Меню"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-[20px] text-[#333333] dark:text-[#EEEEEE]`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        } bg-white dark:bg-[#1A1A1A]`}
      >
        <div className="px-6 pb-6 pt-2 flex flex-col gap-1">
          {[
            { id: 'services', label: t.services },
            { id: 'about', label: t.about },
            { id: 'testimonials', label: t.testimonials },
            { id: 'contact', label: t.contact },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-left py-3 text-[16px] text-[#333333] dark:text-[#CCCCCC] border-b border-[#F0F0F0] dark:border-[#2A2A2A] hover:text-[#7EC8E3] transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={() => scrollToSection('contact')}
              className="flex-1 py-3 bg-[#7EC8E3] text-white rounded-full text-[15px] font-medium hover:bg-[#6BB5D0] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t.cta}
            </button>

            {/* Theme toggle — mobile menu */}
            <button
              onClick={() => setIsDark(!isDark)}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F1F1F1] dark:bg-[#2A2A2A] cursor-pointer transition-colors duration-200 shrink-0"
              aria-label="Переключить тему"
            >
              <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-[20px] text-[#333333] dark:text-[#EEEEEE]`}></i>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
